import { FC } from 'react';
import KpiCard from '@/components/admin/KpiCard';
import type { MaquinaSerie, SituacaoOpMaquina } from '@/types/dashboards';
import { piorMaquina, ultimaSemanaComMfr, type ResumoSituacao } from '@/utils/equiplexMaquinas';
import { fmtCompacto, fmtDiaMes, fmtPct, fmtUn } from '@/utils/painelFormat';
import LinhaRegua from '../LinhaRegua';

interface Props {
  total: MaquinaSerie | null;
  maquinas: MaquinaSerie[];
  resumo: Record<SituacaoOpMaquina, ResumoSituacao>;
}

function subMes(total: MaquinaSerie | null): string {
  if (!total || total.mes_total.dias_fechados === 0) return 'nenhum dia fechado no mês';
  const t = total.mes_total;
  return `${t.dias_fechados} dias fechados · ${fmtCompacto(t.produzido)} de ${fmtCompacto(t.programado)}`;
}

/** Os números que a reunião lê primeiro: mês, semana, a pior máquina e as OPs que pedem ação. */
const KpisMaquinas: FC<Props> = ({ total, maquinas, resumo }) => {
  const semana = ultimaSemanaComMfr(total);
  const pior = piorMaquina(maquinas);
  const semApontamento = {
    ops: resumo.nao_iniciada.ops + resumo.iniciada_sem_apontamento.ops,
    programado: resumo.nao_iniciada.programado + resumo.iniciada_sem_apontamento.programado,
  };
  const encerradas = resumo.encerrada_sem_produzir;
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(15rem,1fr))] gap-4" data-testid="maq-kpis">
      <KpiCard icon="speed" value={fmtPct(total?.mes_total.mfr ?? null)} label="MFR do mês" sublabel={subMes(total)}>
        <LinhaRegua>
          Produzido = reportado nas OPs que começaram no dia (o CP0511 sem reprocesso), a mesma régua da Visão geral e do
          S&OP.
        </LinhaRegua>
      </KpiCard>
      <KpiCard
        icon="date_range"
        value={fmtPct(semana?.mfr ?? null)}
        label={semana && !semana.completa ? 'MFR da semana (aberta)' : 'MFR da semana'}
        sublabel={semana ? `${fmtDiaMes(semana.inicio)} a ${fmtDiaMes(semana.fim)}` : 'sem dia fechado'}
      />
      <KpiCard
        icon="trending_down"
        value={pior ? fmtPct(pior.mes_total.mfr) : '—'}
        label="Pior máquina no mês"
        sublabel={pior?.maquina ?? 'sem dia fechado'}
      />
      <KpiCard
        icon="report"
        value={fmtUn(semApontamento.ops)}
        label="OPs sem apontamento"
        sublabel={
          semApontamento.ops ? `${fmtCompacto(semApontamento.programado)} unidades programadas` : 'toda OP de dia fechado foi reportada'
        }
      />
      <KpiCard
        icon="block"
        value={fmtUn(encerradas.ops)}
        label="Encerradas sem produzir"
        sublabel={encerradas.ops ? `fora do programado · ${fmtCompacto(encerradas.programado)} un` : 'nenhuma no mês'}
      />
    </div>
  );
};

export default KpisMaquinas;
